import { Fragment } from 'react';
import { Button, Menu, MenuDivider, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import './PickList.css';

export interface PickOption {
  value: string;
  label: string;
  /** Options with the same group are listed together under that title. */
  group?: string;
  disabled?: boolean;
}

interface PickListProps {
  value: string;
  options: PickOption[];
  onChange: (value: string) => void;
  /** Shown when the value matches no option. */
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  fill?: boolean;
  disabled?: boolean;
  large?: boolean;
  /** Borderless trigger, for an action such as "Add rep". */
  minimal?: boolean;
  /** Search box above the list; on by default for lists longer than eight. */
  filterable?: boolean;
  className?: string;
}

/**
 * A single-choice list with the CRM record form's picklist look: a field-style
 * button with a double caret that opens a searchable menu. Use it in place of
 * a native select so lists match the rest of the CRM.
 */
export function PickList({
  value, options, onChange, placeholder = 'Choose…', ariaLabel, id, fill = false, disabled = false,
  large = false, minimal = false, filterable, className = '',
}: PickListProps) {
  const selected = options.find((o) => o.value === value);
  return (
    <Select<PickOption>
      items={options}
      activeItem={selected ?? null}
      disabled={disabled}
      filterable={filterable ?? options.length > 8}
      fill={fill}
      inputProps={{ placeholder: 'Search…', small: true }}
      popoverProps={{ portalClassName: 'sky-elevated-portal', minimal: true, matchTargetWidth: fill }}
      itemPredicate={(query, o) => o.label.toLowerCase().includes(query.toLowerCase())}
      itemListRenderer={({ filteredItems, renderItem, itemsParentRef, menuProps }) => (
        <Menu {...menuProps} ulRef={itemsParentRef} className="sky-picklist__menu">
          {filteredItems.length === 0 ? <MenuItem disabled text="No matches" /> : filteredItems.map((o, i) => (
            <Fragment key={o.value}>
              {o.group && o.group !== filteredItems[i - 1]?.group && <MenuDivider title={o.group} />}
              {renderItem(o, i)}
            </Fragment>
          ))}
        </Menu>
      )}
      itemRenderer={(o, { handleClick, handleFocus, modifiers, ref }) => (
        <MenuItem key={o.value} ref={ref} text={o.label} active={modifiers.active} disabled={modifiers.disabled}
          roleStructure="listoption" selected={o.value === value} onClick={handleClick} onFocus={handleFocus} data-value={o.value} />
      )}
      itemDisabled={(o) => Boolean(o.disabled)}
      onItemSelect={(o) => onChange(o.value)}
    >
      <Button
        id={id}
        aria-label={ariaLabel}
        data-value={value}
        className={`sky-picklist${minimal ? ' sky-picklist--minimal' : ''}${large ? ' sky-picklist--large' : ''}${selected ? '' : ' sky-select-placeholder'} ${className}`}
        text={selected?.label ?? placeholder}
        title={selected?.label}
        rightIcon="double-caret-vertical"
        fill={fill}
        disabled={disabled}
      />
    </Select>
  );
}
